import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowBack, ArticleOutlined, Event, Refresh, Warning } from "@mui/icons-material";
import { useReprocessTranscript, useTranscript } from "@/api/hooks/transcripts";
import { TRANSCRIPT_STATUS_LABELS } from "@/api/labels";
import type { TranscriptDetail as TranscriptDetailType, TranscriptStatus } from "@/api/types";
import ActionItemRow from "./ActionItemRow";
import RiskRow from "./RiskRow";

const STATUS_COLOR: Record<
  TranscriptStatus,
  "default" | "info" | "success" | "error"
> = {
  pending: "info",
  processing: "info",
  complete: "success",
  failed: "error",
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function TranscriptDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useTranscript(id);
  const reprocess = useReprocessTranscript(id ?? "");

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Alert severity="error">
          {error?.message ?? "Transcript not found."}
        </Alert>
      </Box>
    );
  }

  const filename = data.s3_key.split("/").pop() || data.s3_key;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton
          onClick={() =>
            data.site_id ? navigate(`/clinops/sites/${data.site_id}`) : navigate("/clinops/sites")
          }
          aria-label="Back"
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ArticleOutlined color="primary" />
            <Typography variant="h5" sx={{ wordBreak: "break-all" }}>
              {filename}
            </Typography>
            <Chip
              size="small"
              label={TRANSCRIPT_STATUS_LABELS[data.status]}
              color={STATUS_COLOR[data.status]}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Uploaded {formatDateTime(data.uploaded_at)}
            {data.site_id && (
              <>
                {" · "}
                <Link
                  to={`/clinops/sites/${data.site_id}`}
                  style={{ color: "inherit" }}
                >
                  View site
                </Link>
              </>
            )}
          </Typography>
        </Box>
      </Box>

      {(data.status === "pending" || data.status === "processing") && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Extracting insights from the transcript. This typically takes
              15–30 seconds.
            </Typography>
            <LinearProgress />
          </CardContent>
        </Card>
      )}

      {data.status === "failed" && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<Refresh />}
              disabled={reprocess.isPending}
              onClick={() => reprocess.mutate()}
            >
              {reprocess.isPending ? "Retrying…" : "Retry"}
            </Button>
          }
        >
          Extraction failed: {data.error_message ?? "unknown error"}
        </Alert>
      )}

      {data.summary && <SummaryCard detail={data} />}
      {data.status === "complete" && (
        <>
          <ActionItemsCard detail={data} />
          <KeyDatesCard detail={data} />
          <RisksCard detail={data} />
        </>
      )}
    </Box>
  );
}

function SummaryCard({ detail }: { detail: TranscriptDetailType }) {
  if (!detail.summary) return null;
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          Summary
        </Typography>
        <Typography sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
          {detail.summary.summary_text}
        </Typography>
      </CardContent>
    </Card>
  );
}

function ActionItemsCard({ detail }: { detail: TranscriptDetailType }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          Action items ({detail.action_items.length})
        </Typography>
        {detail.action_items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No action items were identified.
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            {detail.action_items.map((a) => (
              <ActionItemRow key={a.id} item={a} transcriptId={detail.id} />
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function KeyDatesCard({ detail }: { detail: TranscriptDetailType }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          Key dates ({detail.key_dates.length})
        </Typography>
        {detail.key_dates.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No key dates were identified.
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            {detail.key_dates.map((k) => (
              <Box
                key={k.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Event color="primary" />
                <Typography sx={{ fontWeight: 600, minWidth: 120 }}>
                  {formatDate(k.event_date)}
                </Typography>
                <Typography variant="body2">{k.description}</Typography>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function RisksCard({ detail }: { detail: TranscriptDetailType }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          <Warning fontSize="inherit" sx={{ verticalAlign: "middle", mr: 0.5 }} />
          Risks ({detail.risks.length})
        </Typography>
        {detail.risks.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No risks or issues were identified.
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            {detail.risks.map((r) => (
              <RiskRow key={r.id} risk={r} transcriptId={detail.id} />
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
